import { Cancel } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { markAsLostBatchProcess } from 'lib/api/sgr';
import { SGR_LOST_REASONS } from 'lib/consts';
import CustomButton from 'lib/designSystem/Button';
import DialogTitle from 'lib/designSystem/Title/DialogTitle';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  open: boolean;
  onClose: () => void;
  selectedIds: number[];
  onSuccess: () => void;
}

const BatchMarkAsLostDialog = ({
  open,
  onClose,
  selectedIds,
  onSuccess,
}: Props) => {
  const dispatch = useDispatch();
  const [lostReason, setLostReason] = useState<string>('');
  const [lostDescription, setLostDescription] = useState<string>('');
  const { mutateAsync, isPending } = markAsLostBatchProcess(dispatch);

  const handleConfirm = async () => {
    if (!lostReason) {
      showAlert(dispatch, 'error', 'Debes seleccionar un motivo de pérdida');
      return;
    }
    try {
      await mutateAsync({
        ids: selectedIds,
        lostReason,
        lostDescription: lostDescription || null,
      });
      showAlert(
        dispatch,
        'success',
        `${selectedIds.length} garantía(s) marcadas como perdidas`,
      );
      onSuccess();
      onClose();
    } catch (error) {
      errorHandler(dispatch, error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle
        onClose={onClose}
        title="Marcar como Perdidas"
        icon={<Cancel color="error" />}
        color="error"
      />
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se marcarán <strong>{selectedIds.length}</strong> garantía(s) como
          perdidas con el mismo motivo y descripción.
        </Typography>
        <Stack direction="column" spacing={2} py={1}>
          <TextField
            select
            fullWidth
            label="Motivo de la pérdida"
            value={lostReason}
            onChange={(e) => setLostReason(e.target.value)}
            required
          >
            {SGR_LOST_REASONS.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            label="Descripción adicional"
            placeholder="Describe el motivo de la pérdida (opcional)"
            value={lostDescription}
            onChange={(e) => setLostDescription(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton color="error" onClick={handleConfirm} loading={isPending}>
          Confirmar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default BatchMarkAsLostDialog;
